import styled from '@emotion/styled';
import * as React from 'react';
import { PillButtonFrame } from './PillPrimitives';

export interface LanguageTabProps {
  children?: React.ReactNode;
  label: string;
}

export interface LanguageTabsProps {
  defaultValue?: string;
  label?: string;
}

interface ParsedLanguageTab {
  content: React.ReactNode;
  label: string;
}

export const LanguageTab: React.FC<LanguageTabProps> = ({ children }) => {
  return <>{children}</>;
};

const isLanguageTabElement = (
  child: React.ReactNode
): child is React.ReactElement<LanguageTabProps, typeof LanguageTab> => {
  return React.isValidElement(child) && child.type === LanguageTab;
};

export const LanguageTabs: React.FC<React.PropsWithChildren<LanguageTabsProps>> = ({
  children,
  defaultValue,
  label = 'Choose language'
}) => {
  const baseId = React.useId();
  const tabs = React.useMemo<ParsedLanguageTab[]>(
    () =>
      React.Children.toArray(children)
        .filter(isLanguageTabElement)
        .map((child) => ({
          content: child.props.children,
          label: child.props.label
        })),
    [children]
  );

  const defaultIndex = React.useMemo(() => {
    if (tabs.length === 0) {
      return -1;
    }

    if (!defaultValue) {
      return 0;
    }

    const matchedIndex = tabs.findIndex((tab) => tab.label === defaultValue);
    return matchedIndex >= 0 ? matchedIndex : 0;
  }, [defaultValue, tabs]);

  const [selectedIndex, setSelectedIndex] = React.useState(defaultIndex);

  React.useEffect(() => {
    setSelectedIndex(defaultIndex);
  }, [defaultIndex]);

  if (tabs.length === 0 || selectedIndex < 0) {
    return null;
  }

  const selectedTab = tabs[selectedIndex];

  return (
    <S.Root data-content-language-tabs>
      <S.Header data-tabs-header>
        <S.TabList aria-label={label} role="tablist">
          {tabs.map((tab, index) => {
            const tabId = `${baseId}-${index}`;
            const panelId = `${tabId}-panel`;
            const active = index === selectedIndex;

            return (
              <S.TabButton
                key={tab.label}
                aria-controls={panelId}
                aria-selected={active}
                id={tabId}
                onClick={() => setSelectedIndex(index)}
                onKeyDown={(event) => {
                  const next =
                    event.key === 'ArrowRight'
                      ? (index + 1) % tabs.length
                      : event.key === 'ArrowLeft'
                        ? (index + tabs.length - 1) % tabs.length
                        : event.key === 'Home'
                          ? 0
                          : event.key === 'End'
                            ? tabs.length - 1
                            : undefined;
                  if (next === undefined) return;
                  event.preventDefault();
                  setSelectedIndex(next);
                  event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
                }}
                role="tab"
                tabIndex={active ? 0 : -1}
                type="button"
                active={active}
              >
                {tab.label}
              </S.TabButton>
            );
          })}
        </S.TabList>
      </S.Header>
      <S.Panel aria-labelledby={`${baseId}-${selectedIndex}`} id={`${baseId}-${selectedIndex}-panel`} role="tabpanel">
        <S.PanelContent>{selectedTab.content}</S.PanelContent>
      </S.Panel>
    </S.Root>
  );
};

namespace S {
  export const Root = styled.aside`
    position: relative;
    margin: 1em 0;
    border: 1px solid ${(p) => p.theme.text.codeBlockBorder};
    border-radius: var(--content-list-radius);
    background: ${(p) => p.theme.text.codeBlockBackground};
    box-shadow: ${(p) => p.theme.text.codeBlockShadow};
    overflow: hidden;

    [data-code-block] {
      position: static;
      margin: 0;
    }
    && pre {
      border: 0;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
    }
    &:hover [data-code-copy],
    &:focus-within [data-code-copy] {
      opacity: 1;
    }
  `;

  export const Header = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 8px 48px 0 8px;
  `;

  export const TabList = styled.div`
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    height: 30px;
    gap: 2px;
    padding: 2px;
    border: 1px solid ${(p) => p.theme.text.listBorder};
    border-radius: 999px;
    background: ${(p) => p.theme.text.listBackground};
  `;

  export const TabButton = styled(PillButtonFrame)<{ active: boolean }>`
    min-height: 24px;
    height: 24px;
    padding: 0 8px;
    line-height: 1;
    background: ${(p) => (p.active ? p.theme.text.heading : 'transparent')};
    color: ${(p) => (p.active ? (p.theme.light ? '#ffffff' : '#111111') : p.theme.text.paragraph)};
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;

    &:hover {
      transition:
        background 0.2s ease,
        color 0.2s ease;
      color: ${(p) => (p.active ? (p.theme.light ? '#ffffff' : '#111111') : p.theme.text.heading)};
    }
  `;

  export const Panel = styled.div`
    padding: 0;
  `;

  export const PanelContent = styled.div`
    > * {
      margin-top: 18px;
    }

    > :first-child {
      margin-top: 0;
    }

    p {
      margin-top: 0;
    }

    pre {
      margin-top: 0;
    }
  `;
}
